import { UniButton } from '@/design-system/components/buttons';
import { H6, TextBody, TextCaption } from '@/design-system/components/typography';
import { ScrollView, SxProp, View } from 'dripsy';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/design-system/components/icons/Icon';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { TopUpStackParamList } from '@/features/account-management/screens/top-up/type';
import moment from 'moment-timezone';
import { formatAmountByDecimal } from '@/features/account-management/utils';

export interface TopUpProps {
  onClose: () => void;
  data: any;
  onLayout: (event: any) => void;
  returnData?: any;
  refresh?: () => void;
  record?: any;
}

export const TopUpWebTwoScreen: React.FC<TopUpProps> = (props) => {
  const { onClose, data, onLayout, returnData, refresh, record } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const {
    topHeader,
    body,
    horizontalWrapper,
    textLabel,
    textDebitAmount,
    textTitle,
    textAtmPin,
    btn,
    textHeaderTitle,
    textHeaderSubTitle
  } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<TopUpStackParamList>>();

  const [instructionTime, setInstructionTime] = useState(moment().format('YYYY-MM-DD HH:mm:ss'));

  return (
    <View
      sx={{
        height: layout.isMobile ? '100%' : 'auto',
        width: layout.dimensions.width,
        maxWidth: layout.isMobile ? layout.dimensions.width : Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <View sx={topHeader}>
        <Icon name="checkCircleBlue" height={90} width={90} />
        <H6 bold sx={textHeaderTitle}>
          {t('card-account-management::top-up-placed.title--top-up-account-placed')}
        </H6>
        <TextCaption sx={textHeaderSubTitle}>{t('card-account-management::top-up-placed.text--you-have-topped-up')}</TextCaption>
      </View>
      <ScrollView sx={{ flex: 1, backgroundColor: 'transparent' }} nestedScrollEnabled>
        <View sx={body}>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account-management::glossary.from-account')}
            </TextCaption>
            <H6 sx={{ ...textAtmPin, mb: '$1' }}>{data.wallet.label}</H6>
            <TextBody>{data.wallet.accountNumber}</TextBody>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account-management::glossary.to-account')}
            </TextCaption>
            <H6 sx={{ ...textAtmPin, mb: '$1' }}>
              {t('card-account-management::glossary.card-account')} - {record?.name}
            </H6>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account-management::glossary.amount')}
            </TextCaption>
            <H6 sx={textAtmPin}>
              {formatAmountByDecimal({ value: data.amount, decimals: 2 })} {record.currency}
            </H6>
          </View>
          <View>
            <TextCaption sx={textTitle} bold>
              {t('card-account-management::glossary.xmetadata')}
            </TextCaption>
            {Object.keys(data.xmetadata).map((k) => (
              <View sx={{ pl: '$5', my: '$3' }}>
                <TextCaption sx={textTitle} semiBold>
                  {k}
                </TextCaption>
                <H6 sx={textAtmPin}>{data.xmetadata[k]}</H6>
              </View>
            ))}
          </View>
          <View sx={horizontalWrapper}>
            <TextBody sx={textLabel}>{t('card-account-management::glossary.instruction-time')}</TextBody>
            <TextBody sx={textDebitAmount}>{returnData?.instructionTime || instructionTime || '-'}</TextBody>
          </View>
          <UniButton
            sx={btn}
            labelSx={{ color: '$balck' }}
            label={t('card-account-management::general.button--close')}
            onPress={() => {
              refresh && refresh();
              navigation.getParent()?.goBack();
            }}
          />
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  topHeader: {
    justifyContent: 'center',
    alignItems: 'center',
    background: 'primaryDefault',
    mt: '$10'
  },
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28,
    gap: '$5'
  },
  horizontalWrapper: {
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  textLabel: {
    color: '$textSecondary',
    mb: '$1'
  },
  textDebitAmount: {
    mb: '$1',
    color: '$textPrimary'
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btn: {
    width: '100%',
    height: 44
  },
  textHeaderTitle: {
    mb: '$2'
  },
  textHeaderSubTitle: {
    color: '$textPrimary'
  }
};
